import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

// Aligns with the badge palette on the dashboard so legend/donut/badges all match.
const INDIVIDUAL_COLORS = {
  taster: '#3b82f6',      // blue-500
  collector: '#a855f7',   // purple-500
  enthusiast: '#ec4899',  // pink-500
  connoisseur: '#f59e0b', // amber-500
  vip: '#dc2626',         // red-600
  free: '#e5e7eb',        // gray-200
};

const LISTING_COLORS = {
  claimed: '#9ca3af',     // gray-400
  enhanced: '#3b82f6',    // blue-500
  premium: '#a855f7',     // purple-500
  reserve: '#dc2626',     // red-600
  free: '#e5e7eb',        // gray-200
};

function CustomTooltip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const slice = payload[0];
  const pct = total > 0 ? Math.round((slice.value / total) * 100) : 0;
  return (
    <div className="bg-white border border-gray-200 rounded shadow px-3 py-2 text-sm">
      <div className="font-medium text-gray-900">{slice.name}</div>
      <div className="text-gray-600">{slice.value} ({pct}%)</div>
    </div>
  );
}

/**
 * Donut for a subscription tier mix.
 *
 * @param {string} kind            'individual' | 'listing'
 * @param {Array}  tiers           [{ tier, name, count }, ...] (paid only)
 * @param {number} freeCount       free-tier user/listing count (shown as a greyed-out slice)
 * @param {Function} [onTierClick] (tier, kind) => void — for drill-down
 */
export default function TierDonut({ kind, tiers, freeCount = 0, onTierClick }) {
  const colorMap = kind === 'listing' ? LISTING_COLORS : INDIVIDUAL_COLORS;

  const data = [
    ...(tiers || []).map((t) => ({
      key: t.tier,
      name: t.name || t.tier,
      value: t.count,
      color: colorMap[t.tier] || '#6b7280',
    })),
    ...(freeCount > 0
      ? [{ key: 'free', name: 'Free', value: freeCount, color: colorMap.free }]
      : []),
  ];

  const total = data.reduce((sum, d) => sum + d.value, 0);

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">
        No data yet
      </div>
    );
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={95}
            paddingAngle={1}
            onClick={(entry) => onTierClick && entry?.key !== 'free' && onTierClick(entry.key, kind)}
            style={{ cursor: onTierClick ? 'pointer' : 'default' }}
          >
            {data.map((entry) => (
              <Cell key={entry.key} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip total={total} />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
        {data.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => onTierClick && entry.key !== 'free' && onTierClick(entry.key, kind)}
            className={`inline-flex items-center text-xs ${
              onTierClick && entry.key !== 'free' ? 'hover:underline cursor-pointer' : 'cursor-default'
            }`}
          >
            <span
              className="inline-block w-3 h-3 rounded-sm mr-1.5"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700">{entry.name}</span>
            <span className="text-gray-400 ml-1">({entry.value})</span>
          </button>
        ))}
      </div>
    </div>
  );
}
